import type { ReactNode } from "react"
import { IconAtom2Outline18 as AiIcon } from "nucleo-ui-outline-18"

import type { FeedbackOrigin } from "@productlane/db/browser"
import { FeedbackOriginTextRecord } from "@productlane/lib"
import { cn } from "@productlane/lib/src/cn"
import { formatReadableTimestamp } from "@productlane/lib/src/date"
import {
	Avatar,
	Badge,
	Dialog,
	Loader,
	MESSAGE_SURFACE_GUTTER_CLASS,
	typographyVariants,
} from "@productlane/ui"

import type { AgentBucket } from "./agent-buckets"
import { AgentMessageAvatar } from "@/screens/app/threads/components/center-content/timeline-events/base/agent-message-avatar"
import { SlackStyleMessage } from "@/screens/app/threads/components/center-content/timeline-events/base/slack-style-message"
import { agentBucketColumns } from "./agent-buckets"
import {
	conversationMessageSource,
	groupConsecutiveMessages,
} from "./agent-conversation-groups"

export interface AgentConversationMessage {
	id: string
	role: "USER" | "ASSISTANT"
	createdAt: Date | string
	text: string
}

export interface AgentConversation {
	createdAt: Date | string
	bucket: AgentBucket | null
	threadOrigin: FeedbackOrigin | null
	contactName: string | null
	contactEmail: string | null
	contactImageUrl: string | null
	contactIsUnverified: boolean
	companyName: string | null
	companyLogoUrl: string | null
	messages: AgentConversationMessage[]
}

const AGENT_SENDER_NAME = "Productlane Agent"

function ConversationMessageEvent({
	message,
	conversation,
	showAvatar,
}: {
	message: AgentConversationMessage
	conversation: AgentConversation
	showAvatar: boolean
}) {
	const isAgent = message.role === "ASSISTANT"

	return (
		<SlackStyleMessage
			showAvatar={showAvatar}
			senderName={
				isAgent ? AGENT_SENDER_NAME : (conversation.contactName ?? undefined)
			}
			senderEmail={isAgent ? undefined : conversation.contactEmail}
			senderImage={isAgent ? undefined : conversation.contactImageUrl}
			companyName={isAgent ? undefined : conversation.companyName}
			companyLogoUrl={isAgent ? undefined : conversation.companyLogoUrl}
			senderIsUnverified={!isAgent && conversation.contactIsUnverified}
			timestamp={new Date(message.createdAt)}
			avatarContent={isAgent ? <AgentMessageAvatar /> : undefined}
			source={conversationMessageSource(conversation.threadOrigin)}
			isOutbound={isAgent}
		>
			<div
				className={typographyVariants({
					variant: "message",
					className: "min-w-full overflow-auto whitespace-pre-wrap",
				})}
			>
				{message.text || "(empty message)"}
			</div>
		</SlackStyleMessage>
	)
}

export function AgentConversationDialog({
	isOpen,
	onClose,
	conversation,
	isLoading,
	heading,
	headingImageUrl,
	footer,
}: {
	isOpen: boolean
	onClose: () => void
	conversation: AgentConversation | undefined
	isLoading: boolean
	heading: string
	headingImageUrl?: string | null
	footer?: ReactNode
}) {
	const column = agentBucketColumns.find(
		(entry) => entry.bucket === conversation?.bucket,
	)
	const OutcomeIcon = column?.icon

	return (
		<Dialog
			isOpen={isOpen}
			onOpenChange={(open) => {
				if (!open) onClose()
			}}
			size="lg"
			title={
				<span className="flex items-center gap-2">
					{headingImageUrl !== undefined && conversation ? (
						<Avatar size="xs" name={heading} image={headingImageUrl} />
					) : (
						<AiIcon className="h-4 w-4 flex-none text-tertiary" />
					)}
					<span className="truncate">{heading}</span>
					{OutcomeIcon && conversation?.bucket ? (
						<Badge
							variant={conversation.bucket === "escalated" ? "red" : "default"}
							size="sm"
						>
							<OutcomeIcon className="h-3 w-3 flex-none" />
							{column?.label}
						</Badge>
					) : null}
				</span>
			}
			description={
				conversation
					? [
							conversation.threadOrigin
								? FeedbackOriginTextRecord[conversation.threadOrigin]
								: null,
							formatReadableTimestamp(conversation.createdAt),
							`${conversation.messages.length} messages`,
						]
							.filter(Boolean)
							.join(" · ")
					: undefined
			}
			scrollContainerClasses="max-h-[70dvh]"
			footer={footer}
		>
			{isLoading ? (
				<div className="flex h-40 items-center justify-center">
					<Loader />
				</div>
			) : !conversation || conversation.messages.length === 0 ? (
				<div className="flex h-40 items-center justify-center text-sm text-tertiary">
					This conversation has no messages left to show
				</div>
			) : (
				<div className={cn("w-full pb-2", MESSAGE_SURFACE_GUTTER_CLASS)}>
					{groupConsecutiveMessages(conversation.messages).map((group) => (
						<div key={group[0]?.id} className="my-2 flex flex-col gap-1">
							{group.map((message, index) => (
								<ConversationMessageEvent
									key={message.id}
									message={message}
									conversation={conversation}
									showAvatar={index === 0}
								/>
							))}
						</div>
					))}
				</div>
			)}
		</Dialog>
	)
}
